import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowUpDownIcon from '@mui/icons-material/ImportExport';
import SearchIcon from '@mui/icons-material/Search';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type {
  GridColDef,
  GridFilterItem,
  GridFilterModel,
  GridPaginationModel,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import {
  NAVY,
  RADIUS_CARD,
  SHADOW,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
  SURFACE_HOVER,
  SURFACE_TINT,
} from 'styles/tokens';

import ColumnOrderModal from '../ColumnOrderModal';
import FilterModal from '../FilterModal';
import PaginationFooter from '../PaginationFooter';

const COLUMN_ORDER_STORAGE_KEY = 'custom_data_grid_column_preferences';

interface ColumnPreferences {
  columnOrder: string[];
  hiddenColumns: string[];
  columnWidths: { [field: string]: number };
  lastUpdated?: number;
}
interface StoredPreferences {
  [pageId: string]: ColumnPreferences;
}

const loadColumnPreferences = (columns: GridColDef[], pageId: string): ColumnPreferences => {
  const stored = localStorage.getItem(COLUMN_ORDER_STORAGE_KEY);
  const defaultOrder = columns.map((col) => col.field);
  const defaultWidths = Object.fromEntries(columns.map((col) => [col.field, col.width || 150]));

  if (!stored) {
    return {
      columnOrder: defaultOrder,
      hiddenColumns: [],
      columnWidths: defaultWidths,
    };
  }

  try {
    const preferences = JSON.parse(stored) as StoredPreferences;
    const pagePreferences = preferences?.[pageId];

    if (!pagePreferences?.columnOrder) {
      return {
        columnOrder: defaultOrder,
        hiddenColumns: [],
        columnWidths: defaultWidths,
      };
    }

    // Ensure all columns are present (in case new columns were added)
    const storedOrder = pagePreferences.columnOrder;
    const storedHidden = pagePreferences.hiddenColumns || [];
    const storedWidths = pagePreferences.columnWidths || {};
    const currentFields = new Set(columns.map((col) => col.field));
    const validStoredFields = storedOrder.filter((field) => currentFields.has(field));
    const validHiddenFields = storedHidden.filter((field) => currentFields.has(field));
    const missingFields = columns.map((col) => col.field).filter((field) => !storedOrder.includes(field));

    // Meta columns that should be grouped together after "Campaña" (businessUnitAd)
    const metaColumns = ['metaCampaignName', 'metaAdName', 'metaAdId'];
    const missingMetaColumns = missingFields.filter((field) => metaColumns.includes(field));
    const otherMissingFields = missingFields.filter((field) => !metaColumns.includes(field));

    // Only reposition Meta columns if businessUnitAd exists in the stored order
    const businessUnitAdIndex = validStoredFields.indexOf('businessUnitAd');
    let finalOrder: string[];

    if (businessUnitAdIndex !== -1) {
      // Remove Meta columns from validStoredFields if they exist (we'll reinsert them together)
      const fieldsWithoutMeta = validStoredFields.filter((field) => !metaColumns.includes(field));
      const existingMetaColumns = validStoredFields.filter((field) => metaColumns.includes(field));
      // Combine missing and existing Meta columns, ensuring correct order
      const allMetaColumns = metaColumns.filter(
        (field) => existingMetaColumns.includes(field) || missingMetaColumns.includes(field)
      );

      // Find businessUnitAd position in fieldsWithoutMeta (should be same relative position)
      const adjustedBusinessUnitAdIndex = fieldsWithoutMeta.indexOf('businessUnitAd');
      finalOrder = [
        ...fieldsWithoutMeta.slice(0, adjustedBusinessUnitAdIndex + 1),
        ...allMetaColumns,
        ...fieldsWithoutMeta.slice(adjustedBusinessUnitAdIndex + 1),
        ...otherMissingFields,
      ];
    } else {
      // If businessUnitAd doesn't exist, keep existing order and just append missing fields
      finalOrder = [...validStoredFields, ...missingFields];
    }

    return {
      columnOrder: finalOrder,
      hiddenColumns: validHiddenFields,
      columnWidths: {
        ...defaultWidths,
        ...Object.fromEntries(Object.entries(storedWidths).filter(([field]) => currentFields.has(field))),
      },
      lastUpdated: pagePreferences.lastUpdated,
    };
  } catch (error) {
    console.error('Error loading column preferences:', error);
    return {
      columnOrder: defaultOrder,
      hiddenColumns: [],
      columnWidths: defaultWidths,
    };
  }
};

const setInitialColumns = (
  columns: GridColDef[],
  columnOrder: string[],
  hiddenColumns: string[],
  columnWidths: { [field: string]: number }
) => {
  return columnOrder
    .map((field) => {
      const col = columns.find((c) => c.field === field) as GridColDef;
      return { ...col, width: columnWidths[field] || col.width || 150 };
    })
    .filter((col) => !hiddenColumns.includes(col.field));
};

interface CustomDataGridProps<T> {
  rows: T[];
  columns: GridColDef[];
  filterModel: GridFilterModel;
  sortModel: GridSortModel;
  onFilterModelChange: (model: GridFilterModel) => void;
  onSortModelChange: (model: GridSortModel) => void;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  rowCount: number;
  onRowClick?: (params: T, event: React.MouseEvent) => void;
  getRowClassName: (params: T) => string;
  topBar?: React.ReactNode;
  pageId: string;
  disableColumnOrder?: boolean;
  disableFilters?: boolean;
  disableSort?: boolean;
  disablePagination?: boolean;
  noResultsMessage?: string;
  isLoading?: boolean;
  /**
   * Reads every column `width` as a share of the container instead of a pixel size, so the table
   * always fits and never scrolls horizontally. Opt-in: grids with more columns than a screen can
   * hold need the scroll, and squeezing them would only ellipsize every cell.
   */
  fitToContainer?: boolean;
}

const CustomDataGrid = <T extends { id: number }>({
  rows,
  columns,
  filterModel,
  sortModel,
  onFilterModelChange,
  onSortModelChange,
  paginationModel,
  onPaginationModelChange,
  rowCount,
  onRowClick,
  getRowClassName,
  topBar,
  pageId,
  disableColumnOrder = false,
  disableFilters = false,
  disableSort = false,
  disablePagination = false,
  noResultsMessage = 'No hay resultados',
  isLoading = false,
  fitToContainer = false,
}: CustomDataGridProps<T>) => {
  const tableBodyRef = useRef<HTMLTableSectionElement | null>(null);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [isFilterModalOpen, setFilterModalOpen] = useState(false);
  const [isColumnOrderModalOpen, setColumnOrderModalOpen] = useState(false);
  const [selectedFilterField, setSelectedFilterField] = useState<string>('');
  const filteredColumns = columns.filter((col) => col.colSpan !== 0);
  // Load column order and visibility from localStorage or use default
  const { columnOrder, hiddenColumns, columnWidths } = loadColumnPreferences(filteredColumns, pageId);
  const [updatedColumns, setUpdatedColumns] = useState<GridColDef[]>(
    setInitialColumns(filteredColumns, columnOrder, hiddenColumns, columnWidths)
  );

  useEffect(() => {
    // If the columns change, update the updatedColumns
    setUpdatedColumns(setInitialColumns(filteredColumns, columnOrder, hiddenColumns, columnWidths));
  }, [columns]);

  // Percentages of the configured total: `tableLayout: fixed` then lays the columns out inside the
  // container instead of letting their pixel sum push the table past it. Resizing keeps working —
  // widening one column re-slices the same 100%.
  const totalColumnWidth = updatedColumns.reduce((total, col) => total + (col.width || 150), 0);
  const columnShare = (column: GridColDef): string => `${((column.width || 150) / totalColumnWidth) * 100}%`;

  const saveColumnPreferences = (preferences: { columnOrder: string[]; hiddenColumns: string[] }) => {
    try {
      const stored = localStorage.getItem(COLUMN_ORDER_STORAGE_KEY);
      const storedPreferences = (stored ? JSON.parse(stored) : {}) as StoredPreferences;

      // Save current column widths
      const currentWidths = Object.fromEntries(
        updatedColumns.map((col) => [col.field, col.width || columnWidths[col.field] || 150])
      );

      storedPreferences[pageId] = {
        columnOrder: preferences.columnOrder,
        hiddenColumns: preferences.hiddenColumns,
        columnWidths: currentWidths,
        lastUpdated: Date.now(),
      };

      localStorage.setItem(COLUMN_ORDER_STORAGE_KEY, JSON.stringify(storedPreferences));

      // Update the columns in the grid
      setUpdatedColumns(
        preferences.columnOrder
          .map((field) => {
            const col = filteredColumns.find((c) => c.field === field) as GridColDef;
            return { ...col, width: currentWidths[field] || col.width || 150 };
          })
          .filter((col) => !preferences.hiddenColumns.includes(col.field))
      );
    } catch (error) {
      console.error('Error saving column preferences:', error);
    }
  };

  const handleSort = (field: string) => {
    const existingSort = sortModel.find((sort) => sort.field === field);
    // change from asc to desc or remove the sort
    let newSortModel = existingSort
      ? sortModel.map((sort) =>
          sort.field === field ? { ...sort, sort: sort.sort === 'asc' ? ('desc' as const) : undefined } : sort
        )
      : [...sortModel, { field, sort: 'asc' as const }];
    newSortModel = newSortModel.filter((sort) => sort.sort !== undefined);
    onSortModelChange(newSortModel);
  };

  const handleFilterChange = (items: GridFilterItem[]) => {
    const newFilterModel = {
      items,
    };
    onFilterModelChange(newFilterModel);
  };

  const openFilterModalWithField = (field: string) => {
    setSelectedFilterField(field);
    setFilterModalOpen(true);
  };

  const getRowValue = (row: T, column: GridColDef) => {
    let value = row[column.field as keyof T] as string;
    if (column.valueGetter) {
      value = column.valueGetter(value as never, row, column, {} as never) as string;
    }
    if (column.valueFormatter && value !== undefined && value !== null) {
      value = column.valueFormatter(value as never, row, column, {} as never) as string;
    }
    if (column.renderCell) {
      return column.renderCell({
        row,
        field: column.field,
        value,
        api: {},
      } as GridRenderCellParams);
    }
    return value || '';
  };

  const getSortIcon = (field: string) => {
    if (disableSort) {
      return null;
    }
    const sort = sortModel.find((s) => s.field === field);
    if (sort) {
      return (
        <Box>
          {sort.sort === 'asc' ? (
            <ArrowUpwardIcon sx={{ mr: 1, cursor: 'pointer' }} onClick={() => handleSort(field)} />
          ) : (
            <ArrowDownwardIcon sx={{ mr: 1, cursor: 'pointer' }} onClick={() => handleSort(field)} />
          )}
        </Box>
      );
    }
    if (columns.find((c) => c.field === field)?.sortable === false) {
      return null;
    }
    return (
      <Box className="sort-on-hover" sx={{ display: 'none' }}>
        <ArrowUpDownIcon
          fontSize="inherit"
          sx={{ mr: 1, cursor: 'pointer', color: 'grey' }}
          onClick={() => handleSort(field)}
        />
      </Box>
    );
  };

  const hasFilter = (field: string) => {
    return filterModel.items.some((item) => item.field === field);
  };

  const getFilterIcon = (field: string) => {
    if (disableFilters || columns.find((c) => c.field === field)?.filterable === false) {
      return null;
    }
    if (hasFilter(field)) {
      return (
        <SearchIcon
          sx={{ mr: 1, cursor: 'pointer', color: 'primary.main' }}
          onClick={() => openFilterModalWithField(field)}
        />
      );
    }
    return (
      <Box className="sort-on-hover" sx={{ display: 'none' }}>
        <Tooltip title="Buscar en esta columna">
          <SearchIcon
            fontSize="inherit"
            sx={{ mr: 1, cursor: 'pointer', color: 'grey' }}
            onClick={(e) => {
              e.stopPropagation();
              openFilterModalWithField(field);
            }}
          />
        </Tooltip>
      </Box>
    );
  };

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    onPaginationModelChange(model);
  };

  const handleResize = (field: string, newWidth: number) => {
    setUpdatedColumns((cols) => cols.map((col) => (col.field === field ? { ...col, width: newWidth } : col)));

    // Save the new width to localStorage
    try {
      const stored = localStorage.getItem(COLUMN_ORDER_STORAGE_KEY);
      const storedPreferences = (stored ? JSON.parse(stored) : {}) as StoredPreferences;
      const currentPreferences = storedPreferences[pageId] || {
        columnOrder,
        hiddenColumns,
        columnWidths: {},
        lastUpdated: Date.now(),
      };

      const newWidths = {
        ...currentPreferences.columnWidths,
        [field]: newWidth,
      };

      storedPreferences[pageId] = {
        ...currentPreferences,
        columnWidths: newWidths,
        lastUpdated: Date.now(),
      };

      localStorage.setItem(COLUMN_ORDER_STORAGE_KEY, JSON.stringify(storedPreferences));
    } catch (error) {
      console.error('Error saving column width:', error);
    }
  };

  const handleMouseDown = (e: React.MouseEvent, field: string) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = updatedColumns.find((col) => col.field === field)?.width || 150;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const newWidth = Math.max(50, startWidth + moveEvent.clientX - startX);
      handleResize(field, newWidth);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  useEffect(() => {
    if (tableBodyRef.current) {
      const selectedRow = tableBodyRef.current.querySelector('.previous');
      if (selectedRow) {
        selectedRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [rows, getRowClassName]);

  const sortTooltipTitle =
    sortModel.length > 0
      ? `Ordenado por: ${sortModel.map((sort) => `${sort.field} ${sort.sort === 'asc' ? '↑' : '↓'}`).join(', ')}`
      : 'Ordenamiento';

  const getRowBackgroundColor = (row: T) => {
    const style: { backgroundColor?: string; borderLeft?: string; transition?: string } = {
      transition: 'background-color 0.15s ease',
    };
    if (hoveredRow === row.id) {
      style.backgroundColor = SURFACE_HOVER;
    }
    const className = getRowClassName(row);
    if (className.includes('previous')) {
      style.backgroundColor = SURFACE_CONTAINER_LOW;
      style.borderLeft = `3px solid ${NAVY}`;
    }
    if (className.includes('discarded')) {
      style.backgroundColor = '#ffebee';
      style.borderLeft = '3px solid #ffcdd2';
    }
    if (className.includes('highlighted-')) {
      style.backgroundColor = `rgba(2, 137, 209, 0.${className.slice(-1)})`;
    }
    return style;
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Jost, sans-serif',
        bgcolor: SURFACE_CONTAINER_LOWEST,
        borderRadius: RADIUS_CARD,
        overflow: 'hidden',
        boxShadow: SHADOW,
      }}
    >
      {/* Control bar */}
      {(!disableColumnOrder || !disableSort || !disableFilters || topBar) && (
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            px: 2,
            py: 1.5,
            alignItems: 'center',
            bgcolor: SURFACE_CONTAINER_LOWEST,
          }}
        >
          {!disableColumnOrder && (
            <Tooltip title="Configurar columnas">
              <SwapHorizIcon
                sx={{
                  cursor: 'pointer',
                  color: NAVY,
                  opacity: 0.4,
                  '&:hover': { opacity: 0.8 },
                  transition: 'opacity 0.15s',
                }}
                onClick={() => setColumnOrderModalOpen(true)}
              />
            </Tooltip>
          )}
          {!disableSort && (
            <Tooltip title={sortTooltipTitle}>
              <Badge
                badgeContent={sortModel.length}
                invisible={sortModel.length === 0}
                sx={{
                  '& .MuiBadge-badge': {
                    bgcolor: NAVY,
                    color: '#fff',
                    fontSize: '0.7rem',
                    minWidth: 18,
                    height: 18,
                    padding: '0 4px',
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '9px',
                  },
                }}
              >
                <ArrowUpDownIcon
                  sx={{ color: sortModel.length > 0 ? NAVY : NAVY, opacity: sortModel.length > 0 ? 1 : 0.4 }}
                />
              </Badge>
            </Tooltip>
          )}
          {!disableFilters && (
            <Tooltip title="Filtros">
              <Badge
                badgeContent={filterModel.items.length}
                invisible={filterModel.items.length === 0}
                sx={{
                  '& .MuiBadge-badge': {
                    bgcolor: NAVY,
                    color: '#fff',
                    fontSize: '0.7rem',
                    minWidth: 18,
                    height: 18,
                    padding: '0 4px',
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '9px',
                  },
                }}
              >
                <SearchIcon
                  sx={{
                    cursor: 'pointer',
                    color: NAVY,
                    opacity: filterModel.items.length > 0 ? 1 : 0.4,
                    '&:hover': { opacity: 0.8 },
                    transition: 'opacity 0.15s',
                  }}
                  onClick={() => setFilterModalOpen(true)}
                />
              </Badge>
            </Tooltip>
          )}
          {topBar}
        </Box>
      )}
      <FilterModal
        open={isFilterModalOpen}
        onClose={() => {
          setFilterModalOpen(false);
          setSelectedFilterField('');
        }}
        onFilterChange={handleFilterChange}
        columns={columns}
        filterModel={{
          items:
            selectedFilterField && !filterModel.items.find((item) => item.field === selectedFilterField)
              ? [...filterModel.items, { field: selectedFilterField, operator: '', value: '' }]
              : filterModel.items,
        }}
      />
      {isColumnOrderModalOpen && (
        <ColumnOrderModal
          open={isColumnOrderModalOpen}
          onClose={() => setColumnOrderModalOpen(false)}
          columns={filteredColumns}
          columnOrder={columnOrder}
          hiddenColumns={hiddenColumns}
          onSavePreferences={saveColumnPreferences}
        />
      )}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, minHeight: 0, overflowX: 'auto', position: 'relative' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed' }}>
            <thead
              style={{
                position: 'sticky',
                top: 0,
                backgroundColor: SURFACE_CONTAINER_LOW,
                zIndex: 1,
              }}
            >
              <tr>
                {updatedColumns.map((column) => (
                  <th
                    key={column.field}
                    style={{
                      width: fitToContainer ? columnShare(column) : column.width,
                      padding: '10px 12px',
                      textAlign: 'left',
                      fontWeight: 600,
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      position: 'relative',
                      color: NAVY,
                      fontSize: '0.8rem',
                      letterSpacing: '0.02em',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        '&:hover .sort-on-hover': {
                          display: 'block',
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          width: 'calc(100% - 15px)',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          color: NAVY,
                        }}
                      >
                        {column.headerName || column.field}
                      </Typography>
                      {getSortIcon(column.field)}
                      {getFilterIcon(column.field)}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: disableColumnOrder ? 'default' : 'col-resize',
                          width: 15,
                          position: 'absolute',
                          right: 0,
                          top: 0,
                          bottom: 0,
                          zIndex: 1,
                          justifyContent: 'center',
                          backgroundColor: SURFACE_CONTAINER_LOW,
                        }}
                        onMouseDown={(e) => !disableColumnOrder && handleMouseDown(e, column.field)}
                      >
                        <Box
                          sx={{
                            width: '1px',
                            height: '50%',
                            backgroundColor: 'rgba(21, 24, 58, 0.1)',
                          }}
                          onMouseDown={(e) => !disableColumnOrder && handleMouseDown(e, column.field)}
                        />
                      </Box>
                    </Box>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody ref={tableBodyRef} className="MuiDataGrid-virtualScroller" style={{ overflowY: 'auto' }}>
              {rows.map((row, rowIndex) => (
                <tr
                  key={row.id}
                  style={{
                    cursor: onRowClick ? 'pointer' : 'default',
                    backgroundColor: rowIndex % 2 === 1 ? SURFACE_TINT : undefined,
                    ...getRowBackgroundColor(row),
                  }}
                  className={getRowClassName(row)}
                  onClick={(event) => onRowClick?.(row, event)}
                  onMouseEnter={() => setHoveredRow(row.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  {updatedColumns.map((column) => (
                    <td
                      key={column.field}
                      style={{
                        padding: '10px 12px',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        color: NAVY,
                        fontSize: '0.85rem',
                      }}
                    >
                      {getRowValue(row, column)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {isLoading && rows.length === 0 && (
            <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress sx={{ color: NAVY }} />
            </Box>
          )}
          {!isLoading && rows.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography sx={{ color: NAVY, opacity: 0.5 }}>{noResultsMessage}</Typography>
            </Box>
          )}
          {isLoading && rows.length > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 0.5,
                bgcolor: 'rgba(249, 250, 253, 0.85)',
                backdropFilter: 'blur(12px)',
                borderRadius: '999px',
                boxShadow: SHADOW,
                pointerEvents: 'none',
              }}
            >
              <CircularProgress size={14} sx={{ color: NAVY }} />
              <Typography sx={{ color: NAVY, fontSize: '0.75rem', fontFamily: 'Jost, sans-serif' }}>
                Cargando…
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      {!disablePagination && (
        <PaginationFooter
          page={paginationModel.page}
          pageSize={paginationModel.pageSize}
          rowCount={rowCount}
          onPageChange={(page) => handlePaginationModelChange({ page, pageSize: paginationModel.pageSize })}
          onPageSizeChange={(pageSize) => handlePaginationModelChange({ page: 0, pageSize })}
        />
      )}
    </Box>
  );
};

export default CustomDataGrid;
